import { useEffect, useState } from 'react';

import type { ContentImportRun } from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Badge, LoadingIndicator, Tooltip, toast } from '@gitbook/spine-react';
import { usePrevious } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiClient, useAssertCurrentOrganizationId } from '../../../state';
import {
    PageGroupHighlight,
    PageGroupHighlightHeader,
    PageGroupHighlightLoadingState,
} from './PageGroupHighlight';

export function ImportPagesGroup(props: {
    children: React.ReactNode;
    importRun: ContentImportRun;
    isEmpty: boolean;
}) {
    const { importRun } = props;
    const organizationId = useAssertCurrentOrganizationId();
    const { status } = importRun;
    const previousStatus = usePrevious(status);

    const [justCompleted, setJustCompleted] = useState(false);

    useEffect(() => {
        if (justCompleted) {
            const timeout = setTimeout(() => {
                setJustCompleted(false);
            }, 3000);
            return () => clearTimeout(timeout);
        }
    }, [justCompleted]);

    useEffect(() => {
        if (
            (previousStatus === 'in-progress' || previousStatus === 'pending') &&
            status === 'completed'
        ) {
            setJustCompleted(true);
        }
    }, [previousStatus, status]);

    const descriptor = status ? getImportDescriptor(importRun) : null;

    return (
        <PageGroupHighlight
            isDisabled={!status || (status === 'completed' && !justCompleted)}
            isActive={status === 'pending' || status === 'in-progress' || justCompleted}
        >
            {descriptor && (
                <PageGroupHighlightHeader>
                    {status === 'in-progress' || status === 'pending' ? (
                        <Tooltip label={descriptor.tooltip ?? null}>
                            <PaletteButton
                                kind="pill"
                                size="xsmall"
                                label={descriptor.label}
                                leadingIcon={descriptor.icon}
                                palette={() => {
                                    return createPalette({
                                        groups: [
                                            {
                                                key: 'default',
                                                commands: [
                                                    {
                                                        key: 'stop',
                                                        title: 'Stop',
                                                        icon: <Icon.X />,
                                                        trigger: () => {
                                                            toast.promise(
                                                                apiClient.imports.cancelImportRun(
                                                                    organizationId,
                                                                    importRun.id
                                                                ),
                                                                {
                                                                    loading: 'Stopping import…',
                                                                    success: 'Import stopped',
                                                                }
                                                            );
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                    });
                                }}
                            />
                        </Tooltip>
                    ) : (
                        <Badge
                            kind={status === 'completed' ? 'success' : 'secondary'}
                            size="xsmall"
                            label={descriptor.label}
                            icon={descriptor.icon}
                        />
                    )}
                </PageGroupHighlightHeader>
            )}
            {props.isEmpty ? <PageGroupHighlightLoadingState /> : props.children}
        </PageGroupHighlight>
    );
}

function getImportDescriptor(importRun: ContentImportRun) {
    const { status } = importRun;

    switch (status) {
        case 'pending':
        case 'in-progress': {
            if (importRun.pages && importRun.pages.length > 0) {
                const { imported, total } = importRun.pages.reduce(
                    (acc, page) => {
                        if (page.type === 'page') {
                            acc.total++;
                            if (page.status === 'complete') {
                                acc.imported++;
                            }
                        }
                        return acc;
                    },
                    { imported: 0, total: 0 }
                );
                return {
                    label: `Importing (${((imported * 100) / total).toFixed(0)}%)`,
                    tooltip: `${imported} out of ${total} pages imported`,
                    icon: <LoadingIndicator />,
                };
            }
            return {
                label: 'Importing…',
                icon: <LoadingIndicator />,
            };
        }
        case 'completed':
            return {
                label: 'Complete',
                icon: <Icon.CheckCircle />,
            };
        case 'failed':
            return {
                label: 'Import failed',
                icon: <Icon.X />,
            };
        case 'cancelled':
            return {
                label: 'Import cancelled',
                icon: <Icon.X />,
            };
        default:
            assertNever(status);
    }
}
